import cn from 'classnames';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TOnChangeValue } from '../DocUploadBlock';

import s from './styles.module.css';
import { ISupplementaryDoc, isUploadRequested } from './supplementary-docs';
import { SupplementaryDocUpload } from './SupplementaryDocUpload';
import { TQuestionAnswer } from './types';

const answers = defineMessages({
  yes: {
    defaultMessage: 'Yes, it is',
    id: 'global_answer_yes_it_is',
    description: 'Positive answer to a yes/no question',
  },
  no: {
    defaultMessage: "No, it isn't",
    id: 'global_answer_no_it_isnt',
    description: 'Negative answer to a yes/no question',
  },
});

interface IProps<TDocType extends string> {
  index: number;
  question: React.ReactNode;
  hint: React.ReactNode;
  doc: ISupplementaryDoc<TDocType>;
  onChangeValue: TOnChangeValue<TDocType>;
  onAnswer: (answer: TQuestionAnswer) => void;
  isUploading: boolean;
  error?: string;
}

const QuestionWithDoc = <TDocType extends string>({
  index,
  question,
  hint,
  doc,
  onChangeValue,
  onAnswer,
  isUploading,
  error,
}: IProps<TDocType>) => {
  const intl = useIntl();
  const { form, answer } = doc;
  const testIdPrefix = `dialog__${form.docType}`;

  const renderAnswer = (value: TQuestionAnswer) => (
    <button
      type="button"
      className={cn(s.answer, { [s.answerSelected]: answer === value })}
      disabled={isUploading}
      onClick={() => onAnswer(value)}
      data-test-id={`${testIdPrefix}__answer-${value}`}
    >
      {intl.formatMessage(answers[value])}
    </button>
  );

  return (
    <div className={s.question} data-test-id={`${testIdPrefix}__question`}>
      <span className={s.questionIndex}>{index}</span>
      <div className={s.questionBody}>
        <div className={s.questionTitle}>{question}</div>
        <div className={s.questionHint}>{hint}</div>
        <div className={s.answers}>
          {renderAnswer('yes')}
          {renderAnswer('no')}
        </div>
        {isUploadRequested(doc) && (
          <SupplementaryDocUpload
            doc={doc}
            onChangeValue={onChangeValue}
            isUploading={isUploading}
            error={error}
          />
        )}
      </div>
    </div>
  );
};

export { QuestionWithDoc };
